import {
  localDateParts,
  recurrencePresets,
  sameRecurrence,
  type ColorToken,
  type CreateTaskBody,
  type FollowUpBody,
  type LocalDateParts,
  type RecurrenceRule,
  type TaskDTO,
  type TaskPriority,
  type TaskType,
  type UpdateTaskBody,
} from '@hearth/shared';
import { pluralize } from '../../lib/format';

/**
 * Pure logic behind the task / calendar entry editor. Dates live in the form as the strings the native inputs
 * use ('YYYY-MM-DD', 'HH:mm') in the browser's zone and are converted with local Date constructors, so all-day
 * entries stay on midnight across DST changes.
 */

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;
const pad = (n: number) => String(n).padStart(2, '0');

/** IANA zone the times are entered in; sent with every save so repeats keep their wall-clock time. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// ------------------------------------------------------------------------------------------ date strings

/** Local 'YYYY-MM-DD'. */
export function toDateInput(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local 'HH:mm'. */
export function toTimeInput(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Local Date from input strings; null while the inputs are incomplete or invalid. */
export function fromInputs(date: string, time = '00:00'): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t) return null;
  const result = new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  return Number.isNaN(result.getTime()) ? null : result;
}

/** Calendar-day arithmetic on 'YYYY-MM-DD' (the local constructor normalizes month ends and DST). */
export function addDays(date: string, days: number): string {
  const d = fromInputs(date);
  return d ? toDateInput(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days)) : date;
}

/** Whole calendar days from `a` to `b` (negative when b is earlier). */
export function daysBetween(a: string, b: string): number {
  const da = fromInputs(a);
  const db = fromInputs(b);
  if (!da || !db) return 0;
  const utc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((utc(db) - utc(da)) / DAY);
}

/** `time` plus minutes, wrapping at midnight ('23:30' + 60 → '00:30'). */
export function addMinutesToTime(time: string, minutes: number): string {
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!t) return time;
  const total = (((Number(t[1]) * 60 + Number(t[2]) + minutes) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

// ------------------------------------------------------------------------------------------ when

/** The "When" section of the editor. */
export type EntryWhen = {
  allDay: boolean;
  /** '' = no date (plain tasks only). */
  startDate: string;
  /** Kept while all-day so switching back restores it. */
  startTime: string;
  /** Timed entries: a block with an end, or a point in time. All-day entries always end. */
  hasEnd: boolean;
  /** Timed: the day the end falls on. All-day: the last day, inclusive. */
  endDate: string;
  endTime: string;
};

export type EntryWire = { dueAt: string | null; endAt: string | null; allDay: boolean };

export const DEFAULT_START_TIME = '09:00';
export const MEETING_DURATION_MINUTES = 60;

/** Entry times as the API sends them → form state. */
export function whenFromWire({ dueAt, endAt, allDay }: EntryWire): EntryWhen {
  const start = dueAt ? new Date(dueAt) : null;
  if (!start || Number.isNaN(start.getTime())) {
    return {
      allDay: false,
      startDate: '',
      startTime: DEFAULT_START_TIME,
      hasEnd: false,
      endDate: '',
      endTime: addMinutesToTime(DEFAULT_START_TIME, 60),
    };
  }
  const startDate = toDateInput(start);
  const end = endAt ? new Date(endAt) : null;
  if (allDay) {
    // The exclusive end is the midnight after the last day; the day holding its previous instant is the last one.
    const lastDay = end && end.getTime() > start.getTime() ? toDateInput(new Date(end.getTime() - 1)) : startDate;
    return {
      allDay: true,
      startDate,
      startTime: DEFAULT_START_TIME,
      hasEnd: false,
      endDate: lastDay < startDate ? startDate : lastDay,
      endTime: addMinutesToTime(DEFAULT_START_TIME, 60),
    };
  }
  const startTime = toTimeInput(start);
  if (end && end.getTime() > start.getTime()) {
    return { allDay: false, startDate, startTime, hasEnd: true, endDate: toDateInput(end), endTime: toTimeInput(end) };
  }
  return { allDay: false, startDate, startTime, hasEnd: false, endDate: startDate, endTime: addMinutesToTime(startTime, 60) };
}

/** Form state → API times. All-day: local midnight of the first day → local midnight after the last day. */
export function whenToWire(when: EntryWhen): EntryWire {
  const start = when.allDay ? fromInputs(when.startDate) : fromInputs(when.startDate, when.startTime);
  if (!start) return { dueAt: null, endAt: null, allDay: false };
  if (when.allDay) {
    const end = fromInputs(addDays(when.endDate || when.startDate, 1));
    return { dueAt: start.toISOString(), endAt: end ? end.toISOString() : null, allDay: true };
  }
  const end = when.hasEnd ? fromInputs(when.endDate, when.endTime) : null;
  return { dueAt: start.toISOString(), endAt: end ? end.toISOString() : null, allDay: false };
}

/** Why the times cannot be saved, or null. */
export function whenError(when: EntryWhen): { field: 'start' | 'end'; message: string } | null {
  if (when.startDate === '') return null;
  if (!fromInputs(when.startDate, when.allDay ? '00:00' : when.startTime)) return { field: 'start', message: 'Enter a valid date and time' };
  if (when.allDay) {
    if (!fromInputs(when.endDate)) return { field: 'end', message: 'Enter a valid date' };
    if (when.endDate < when.startDate) return { field: 'end', message: 'The last day cannot be before the first' };
    return null;
  }
  if (!when.hasEnd) return null;
  const start = fromInputs(when.startDate, when.startTime);
  const end = fromInputs(when.endDate, when.endTime);
  if (!end) return { field: 'end', message: 'Enter a valid date and time' };
  if (start && end.getTime() <= start.getTime()) return { field: 'end', message: 'The end must be after the start' };
  return null;
}

/** New start; the end moves with it so the length of the entry is kept. */
export function moveStart(when: EntryWhen, startDate: string, startTime: string = when.startTime): EntryWhen {
  if (when.allDay) {
    if (!fromInputs(startDate) || !fromInputs(when.startDate)) return { ...when, startDate, startTime, endDate: fromInputs(startDate) ? startDate : when.endDate };
    const span = Math.max(0, daysBetween(when.startDate, when.endDate));
    return { ...when, startDate, startTime, endDate: addDays(startDate, span) };
  }
  const next = fromInputs(startDate, startTime);
  if (!when.hasEnd || !next) return { ...when, startDate, startTime, endDate: when.hasEnd ? when.endDate : startDate };
  const oldStart = fromInputs(when.startDate, when.startTime);
  const oldEnd = fromInputs(when.endDate, when.endTime);
  const duration = oldStart && oldEnd && oldEnd.getTime() > oldStart.getTime() ? oldEnd.getTime() - oldStart.getTime() : MEETING_DURATION_MINUTES * MINUTE;
  const end = new Date(next.getTime() + duration);
  return { ...when, startDate, startTime, endDate: toDateInput(end), endTime: toTimeInput(end) };
}

/** Gives a timed entry an end `minutes` after its start. */
export function addEnd(when: EntryWhen, minutes = MEETING_DURATION_MINUTES): EntryWhen {
  const start = fromInputs(when.startDate, when.startTime);
  if (!start) return { ...when, hasEnd: true, endDate: when.startDate, endTime: addMinutesToTime(when.startTime, minutes) };
  const end = new Date(start.getTime() + minutes * MINUTE);
  return { ...when, hasEnd: true, endDate: toDateInput(end), endTime: toTimeInput(end) };
}

/** Switches between an all-day and a timed entry. `timedMinutes`: length of the block when becoming timed (null = no end). */
export function setAllDay(when: EntryWhen, allDay: boolean, timedMinutes: number | null): EntryWhen {
  if (allDay === when.allDay) return when;
  if (allDay) {
    let endDate = when.hasEnd && when.endDate >= when.startDate ? when.endDate : when.startDate;
    // A block ending exactly at midnight does not touch the next day.
    if (when.hasEnd && when.endTime === '00:00' && endDate > when.startDate) endDate = addDays(endDate, -1);
    return { ...when, allDay: true, hasEnd: false, endDate };
  }
  const timed: EntryWhen = { ...when, allDay: false, hasEnd: false, endDate: when.startDate };
  return timedMinutes === null ? timed : addEnd(timed, timedMinutes);
}

/** Google's default for a new entry: the next half hour. */
export function defaultStart(now: Date): Date {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes() < 30 ? 30 : 60);
  return start;
}

/** Form state for a dated entry starting at `start`, optionally `minutes` long. */
export function whenAt(start: Date, minutes: number | null): EntryWhen {
  const when = whenFromWire({ dueAt: start.toISOString(), endAt: null, allDay: false });
  return minutes === null ? when : addEnd(when, minutes);
}

// ------------------------------------------------------------------------------------------ reminders

export const TIMED_REMINDER_PRESETS = [0, 5, 10, 15, 30, 60, 120, 1440, 2880, 10_080] as const;
/** All-day entries start at local midnight: -540 = on the day at 9:00, 900 = the day before at 9:00. */
export const ALL_DAY_REMINDER_PRESETS = [-540, 900, 2340, 9540] as const;
export const DEFAULT_MEETING_REMINDER = 10;

function spanLabel(minutes: number): string {
  if (minutes % 10_080 === 0) return pluralize(minutes / 10_080, 'week');
  if (minutes % 1440 === 0) return pluralize(minutes / 1440, 'day');
  if (minutes % 60 === 0) return pluralize(minutes / 60, 'hour');
  return pluralize(minutes, 'minute');
}

/** "At the start", "10 minutes before", "1 hour before"; all-day: "On the day at 9:00", "1 day before at 9:00". */
export function reminderLabel(minutes: number, allDay: boolean): string {
  if (!allDay) {
    if (minutes === 0) return 'At the start';
    return minutes > 0 ? `${spanLabel(minutes)} before` : `${spanLabel(-minutes)} after the start`;
  }
  const daysBefore = Math.ceil(minutes / 1440);
  const minuteOfDay = daysBefore * 1440 - minutes;
  const time = `${Math.floor(minuteOfDay / 60)}:${pad(minuteOfDay % 60)}`;
  if (daysBefore === 0) return `On the day at ${time}`;
  if (daysBefore < 0) return `${spanLabel(-daysBefore * 1440)} after at ${time}`;
  return `${spanLabel(daysBefore * 1440)} before at ${time}`;
}

/** Select options: "No reminder" (''), the presets, and the current value when it is not one of them. */
export function reminderOptions(allDay: boolean, current: number | null): { value: string; label: string }[] {
  const presets: readonly number[] = allDay ? ALL_DAY_REMINDER_PRESETS : TIMED_REMINDER_PRESETS;
  const values = current !== null && !presets.includes(current) ? [...presets, current].sort((a, b) => a - b) : presets;
  return [{ value: '', label: 'No reminder' }, ...values.map((value) => ({ value: String(value), label: reminderLabel(value, allDay) }))];
}

/** The closest sensible reminder after switching between all-day and timed. */
export function mapReminder(minutes: number | null, toAllDay: boolean): number | null {
  if (minutes === null) return null;
  if (toAllDay) {
    if (minutes < 1440) return -540;
    if (minutes < 2880) return 900;
    if (minutes < 10_080) return 2340;
    return 9540;
  }
  const daysBefore = Math.ceil(minutes / 1440);
  if (daysBefore <= 0) return DEFAULT_MEETING_REMINDER;
  if (daysBefore === 1) return 1440;
  if (daysBefore < 7) return 2880;
  return 10_080;
}

// ------------------------------------------------------------------------------------------ repeat

export type RepeatChoice = { key: 'none' } | { key: 'custom'; rule: RecurrenceRule } | { key: string; rule: RecurrenceRule };

/** The anchor the recurrence helpers need: the local date of the entry's start. */
export function firstParts(when: EntryWhen): LocalDateParts | null {
  const start = fromInputs(when.startDate);
  return start ? localDateParts(start) : null;
}

export function repeatRule(choice: RepeatChoice): RecurrenceRule | null {
  return 'rule' in choice ? choice.rule : null;
}

/** An existing entry's rule → the matching preset, else Custom. */
export function initialRepeat(rule: RecurrenceRule | null, first: LocalDateParts | null): RepeatChoice {
  if (!rule) return { key: 'none' };
  if (!first) return { key: 'custom', rule };
  const preset = recurrencePresets(first).find((candidate) => sameRecurrence(candidate.rule, rule, first));
  return preset ? { key: preset.key, rule: preset.rule } : { key: 'custom', rule };
}

/** After the start moves, a preset follows the new date ("Weekly on Monday" → "on Tuesday"); custom rules stay. */
export function rebaseRepeat(choice: RepeatChoice, first: LocalDateParts | null): RepeatChoice {
  if (choice.key === 'none' || choice.key === 'custom' || !first || !('rule' in choice)) return choice;
  const preset = recurrencePresets(first).find((candidate) => candidate.key === choice.key);
  return preset ? { key: preset.key, rule: preset.rule } : { key: 'custom', rule: choice.rule };
}

function sameRule(a: RecurrenceRule | null, b: RecurrenceRule | null, first: LocalDateParts | null): boolean {
  if (a === null || b === null) return a === b;
  return first ? sameRecurrence(a, b, first) : JSON.stringify(a) === JSON.stringify(b);
}

// ------------------------------------------------------------------------------------------ payloads

/** Everything the editor can set on an entry. */
export type EntryValues = {
  type: TaskType;
  title: string;
  description: string | null;
  contactId: string | null;
  dueAt: string | null;
  endAt: string | null;
  allDay: boolean;
  location: string | null;
  color: ColorToken | null;
  reminderMinutes: number | null;
  priority: TaskPriority;
  assignedTo: string | null;
  attendeeIds: string[];
  recurrence: RecurrenceRule | null;
};

export function valuesFromTask(task: TaskDTO): EntryValues {
  return {
    type: task.type,
    title: task.title,
    description: task.description,
    contactId: task.contactId,
    dueAt: task.dueAt,
    endAt: task.endAt,
    allDay: task.allDay,
    location: task.location,
    color: task.color,
    reminderMinutes: task.reminderMinutes,
    priority: task.priority,
    assignedTo: task.assignedTo,
    attendeeIds: task.attendeeIds,
    recurrence: task.recurrence,
  };
}

/** POST /api/tasks body. `assignedTo` is left to the server (the current user) when the user cannot assign. */
export function createBody(
  values: EntryValues,
  context: { businessId: string | null; opportunityId: string | null; canAssign: boolean; timeZone: string },
): CreateTaskBody {
  return {
    businessId: context.businessId,
    contactId: context.businessId ? values.contactId : null,
    opportunityId: context.opportunityId,
    type: values.type,
    title: values.title.trim(),
    description: values.description,
    dueAt: values.dueAt,
    endAt: values.endAt,
    allDay: values.allDay,
    location: values.location,
    color: values.color,
    reminderMinutes: values.reminderMinutes,
    priority: values.priority,
    ...(context.canAssign ? { assignedTo: values.assignedTo } : {}),
    attendeeIds: values.attendeeIds,
    recurrence: values.recurrence,
    timeZone: context.timeZone,
  };
}

const sameInstant = (a: string | null, b: string | null) => (a === null || b === null ? a === b : new Date(a).getTime() === new Date(b).getTime());
const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((value) => b.includes(value));

type PatchFields = Omit<UpdateTaskBody, 'scope' | 'timeZone'>;

/** Only the fields that differ from the saved entry (empty object = nothing to save). */
export function changedFields(original: EntryValues, values: EntryValues): PatchFields {
  const patch: PatchFields = {};
  if (values.type !== original.type) patch.type = values.type;
  if (values.title.trim() !== original.title) patch.title = values.title.trim();
  if (values.description !== original.description) patch.description = values.description;
  if (values.contactId !== original.contactId) patch.contactId = values.contactId;
  if (!sameInstant(values.dueAt, original.dueAt)) patch.dueAt = values.dueAt;
  if (!sameInstant(values.endAt, original.endAt)) patch.endAt = values.endAt;
  if (values.allDay !== original.allDay) patch.allDay = values.allDay;
  if (values.location !== original.location) patch.location = values.location;
  if (values.color !== original.color) patch.color = values.color;
  if (values.reminderMinutes !== original.reminderMinutes) patch.reminderMinutes = values.reminderMinutes;
  if (values.priority !== original.priority) patch.priority = values.priority;
  if (values.assignedTo !== original.assignedTo) patch.assignedTo = values.assignedTo;
  if (!sameSet(values.attendeeIds, original.attendeeIds)) patch.attendeeIds = values.attendeeIds;
  const anchor = values.dueAt ? localDateParts(new Date(values.dueAt)) : null;
  if (!sameRule(values.recurrence, original.recurrence, anchor)) patch.recurrence = values.recurrence;
  return patch;
}

// ------------------------------------------------------------------------------------------ display

const LOCALE = 'en-CA';

/**
 * When an entry happens, for lists: "Sep 23, 2026, 2:00 p.m. – 3:00 p.m.", "Sep 23, 2026 · All day",
 * "Sep 23 – Sep 25, 2026". Empty string when the entry has no date.
 */
export function formatEntryTime(entry: Pick<TaskDTO, 'dueAt' | 'endAt' | 'allDay'>): string {
  if (!entry.dueAt) return '';
  const start = new Date(entry.dueAt);
  if (Number.isNaN(start.getTime())) return '';
  const end = entry.endAt ? new Date(entry.endAt) : null;
  const hasEnd = end !== null && !Number.isNaN(end.getTime()) && end.getTime() > start.getTime();
  const date = new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium' });
  const dateTime = new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short' });
  const time = new Intl.DateTimeFormat(LOCALE, { timeStyle: 'short' });
  if (entry.allDay) {
    const lastDay = hasEnd ? new Date(end.getTime() - 1) : start;
    return toDateInput(lastDay) === toDateInput(start) ? `${date.format(start)} · All day` : date.formatRange(start, lastDay);
  }
  if (!hasEnd) return dateTime.format(start);
  return toDateInput(end) === toDateInput(start) ? `${dateTime.format(start)} – ${time.format(end)}` : `${dateTime.format(start)} – ${dateTime.format(end)}`;
}

/** Time of day only, for lists already grouped by day: "10:00 a.m. – 11:00 a.m.", "10:00 a.m.", "All day". */
export function formatEntryClock(entry: Pick<TaskDTO, 'dueAt' | 'endAt' | 'allDay'>): string {
  if (!entry.dueAt) return '';
  if (entry.allDay) return 'All day';
  const start = new Date(entry.dueAt);
  const end = entry.endAt ? new Date(entry.endAt) : null;
  const time = new Intl.DateTimeFormat(LOCALE, { timeStyle: 'short' });
  if (!end || end.getTime() <= start.getTime()) return time.format(start);
  return toDateInput(end) === toDateInput(start) ? `${time.format(start)} – ${time.format(end)}` : formatEntryTime(entry);
}

/** Default title of a new entry of each type. */
export function defaultTitle(type: TaskType): string {
  switch (type) {
    case 'call':
      return 'Call about equipment';
    case 'email':
      return 'Send an email';
    case 'meeting':
      return 'Meet at the restaurant';
    case 'note':
      return 'Note';
    default:
      return 'Follow up';
  }
}

/** What the editor calls the entry: "New meeting", "Edit note", "New task". */
export function entryNoun(type: TaskType): 'note' | 'meeting' | 'task' {
  return type === 'note' ? 'note' : type === 'meeting' ? 'meeting' : 'task';
}

// ------------------------------------------------------------------------------------------ follow-ups

export const FOLLOW_UP_QUICK_PICKS = [
  { key: 'tomorrow', label: 'Tomorrow', days: 1 },
  { key: 'in_2_days', label: 'In 2 days', days: 2 },
  { key: 'next_week', label: 'Next week', days: 7 },
] as const;
export type FollowUpWhen = (typeof FOLLOW_UP_QUICK_PICKS)[number]['key'] | 'pick';

/** The next step scheduled together with a logged call / reply or a sent email. */
export type FollowUpState = {
  enabled: boolean;
  type: TaskType;
  /** null = the caller's default title (e.g. one that follows the email subject). */
  title: string | null;
  when: FollowUpWhen;
  /** Used when `when` is 'pick'. */
  date: string;
  time: string;
  reminderMinutes: number | null;
};

/** Quick picks land at 9:00 local on that day; "Pick…" uses the typed date and time. */
export function followUpStart(state: FollowUpState, now: Date): Date | null {
  const quick = FOLLOW_UP_QUICK_PICKS.find((pick) => pick.key === state.when);
  if (!quick) return fromInputs(state.date, state.time);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + quick.days, 9, 0);
}

/** Initial state: `days` from today at 9:00, as a quick pick when one matches. */
export function initialFollowUp(options: { enabled: boolean; type: TaskType; days: number; now: Date }): FollowUpState {
  const quick = FOLLOW_UP_QUICK_PICKS.find((pick) => pick.days === options.days);
  const day = new Date(options.now.getFullYear(), options.now.getMonth(), options.now.getDate() + options.days);
  return {
    enabled: options.enabled,
    type: options.type,
    title: null,
    when: quick?.key ?? 'pick',
    date: toDateInput(day),
    time: DEFAULT_START_TIME,
    reminderMinutes: null,
  };
}

/** Calls are booked as a 30-minute block and meetings as an hour; other follow-ups are a point in time. */
export function followUpBody(state: FollowUpState, defaultTitleText: string, now: Date): FollowUpBody | null {
  const start = followUpStart(state, now);
  const title = (state.title ?? defaultTitleText).trim();
  if (!state.enabled || !start || title === '') return null;
  const minutes = state.type === 'call' ? 30 : state.type === 'meeting' ? MEETING_DURATION_MINUTES : null;
  return {
    type: state.type,
    title,
    dueAt: start.toISOString(),
    endAt: minutes === null ? null : new Date(start.getTime() + minutes * MINUTE).toISOString(),
    allDay: false,
    reminderMinutes: state.reminderMinutes,
  };
}
